import { CONFIG } from "../../../config";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

const isDevelopment = process.env.NODE_ENV === "development";
const devLog = (message, data) => {
  if (isDevelopment) console.log(message, data);
};

/**
 * Hook to fetch case analytics (all) for a given period/filters
 * Endpoint: /analytics/case-analytics-all
 */
export const useGetCaseAnalyticsAll = (queries = {}, options = {}) => {
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== "object") {
      devLog(
        "[useGetCaseAnalyticsAll] Invalid queries parameter, using empty object",
      );
      return {};
    }
    return queries;
  }, [queries]);

  const queryOptions = useMemo(
    () => ({
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      ...options,
    }),
    [options],
  );

  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["caseAnalyticsAll", ...sortedEntries.flat()];
  }, [validatedQueries]);

  const fetchCaseAnalyticsAll = useCallback(async () => {
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        ),
      );

      devLog("[useGetCaseAnalyticsAll] Calling API with params:", cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/case-analytics-all`,
        { params: cleanParams, timeout: 30000 },
      );

      devLog("[useGetCaseAnalyticsAll] Response:", response.data);

      if (!response.data) throw new Error("No data received from server");
      return response.data;
    } catch (error) {
      const isNetworkError = !error.response && error.code === "ENOTFOUND";
      const isTimeout = error.code === "ECONNABORTED";
      let msg = "Error fetching case analytics (all)";
      if (isNetworkError) msg += ": Network error";
      else if (isTimeout) msg += ": Timeout";
      else if (error.response)
        msg += `: Server responded with ${error.response.status}`;

      console.error(msg, {
        error,
        params: validatedQueries,
        time: new Date().toISOString(),
      });
      throw new Error(msg);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchCaseAnalyticsAll,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) =>
      console.error("useGetCaseAnalyticsAll error", {
        error: error.message,
        queries: validatedQueries,
      }),
    select: (data) => data || null,
  });
};

/**
 * Hook to fetch case analytics (all) for a specific date range
 * Endpoint: /analytics/case-analytics-all
 */
export const useGetCaseAnalyticsAllRange = (queries = {}, options = {}) => {
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== "object") {
      devLog(
        "[useGetCaseAnalyticsAllRange] Invalid queries parameter, using empty object",
      );
      return {};
    }
    return queries;
  }, [queries]);

  const queryOptions = useMemo(
    () => ({
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      ...options,
    }),
    [options],
  );

  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["caseAnalyticsAllRange", ...sortedEntries.flat()];
  }, [validatedQueries]);

  const fetchCaseAnalyticsAll = useCallback(async () => {
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        ),
      );

      devLog(
        "[useGetCaseAnalyticsAllRange] Calling API with params:",
        cleanParams,
      );

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/case-analytics-all`,
        { params: cleanParams, timeout: 30000 },
      );

      devLog("[useGetCaseAnalyticsAllRange] Response:", response.data);

      if (!response.data) throw new Error("No data received from server");
      return response.data;
    } catch (error) {
      const isNetworkError = !error.response && error.code === "ENOTFOUND";
      const isTimeout = error.code === "ECONNABORTED";
      let msg = "Error fetching case analytics (all)";
      if (isNetworkError) msg += ": Network error";
      else if (isTimeout) msg += ": Timeout";
      else if (error.response)
        msg += `: Server responded with ${error.response.status}`;

      console.error(msg, {
        error,
        params: validatedQueries,
        time: new Date().toISOString(),
      });
      throw new Error(msg);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchCaseAnalyticsAll,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) =>
      console.error("useGetCaseAnalyticsAllRange error", {
        error: error.message,
        queries: validatedQueries,
      }),
    select: (data) => data || null,
  });
};
